import { inject, Injectable, signal } from "@angular/core";
import { PopupService } from "src/app/services/popup/popup.service";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { defer, forkJoin, iif, lastValueFrom, map, of, switchMap } from "rxjs";
import { InstallationBackend } from "src/app/backend/api/installation.backend";
import {
  ConnectIntegrationConnectionResult,
  ConnectIntegrationConnectionResultType,
  ConnectIntegrationConnector,
  ConnectIntegrationInfo
} from "src/app/modules/bootstrap/functions/integrations/connect-integration/connect-integration.connector";
import { Connection } from "src/app/backend/models/connection";
import DevExpress from "devextreme";
import { DxFormComponent } from "devextreme-angular";
import { IntegrationCustomConfigurationModel } from "src/app/backend/models/integration";

@Injectable()
export class ConnectIntegrationCustomConnector implements ConnectIntegrationConnector {
  private readonly popupService = inject(PopupService);
  private readonly installationBackend = inject(InstallationBackend);

  params = signal<Array<IntegrationCustomConfigurationModel>>([]);
  dxForm: DxFormComponent | undefined = undefined;

  getItemsByParams(): DevExpress.ui.dxForm.Item[] {
    const items: DevExpress.ui.dxForm.Item[] = [];

    this.params().forEach(({ label, name, type }, index) => {
      items.push({
        dataField: name,
        label: {
          text: sntTranslate(label),
          location: "top",
          showColon: false
        }
      });
    });
    return items;
  }

  connect({
    integration,
    installation,
    isRefresh,
    refreshConnectionId,
    phase
  }: ConnectIntegrationInfo): Promise<ConnectIntegrationConnectionResult> {
    return new Promise<ConnectIntegrationConnectionResult>(async (resolve) => {
      const { configuration } = integration.authentication;

      this.params.set(
        <IntegrationCustomConfigurationModel[]>configuration ?? []
      );

      await this.popupService.createEntity<Record<string, any>>({
        title: sntTranslate(marker("Inserisci le credenziali")),
        modalConfig: {
          dialog: true,
          height: 256,
          icon: "fa-light fa-puzzle-piece-simple"
        },
        onFormReady: (dxFormReadyEvent) => {
          this.dxForm = dxFormReadyEvent.dxForm;
        },
        formItems: [
          {
            itemType: "group",
            name: "params",
            colCount: 1,
            items: this.getItemsByParams()
          }
        ],
        create: async (data) => {
          const connection = await lastValueFrom(
            forkJoin({
              createdConnetion: iif(
                () => !isRefresh,
                defer(() => {
                  return this.installationBackend.createConnection({
                    provider: integration.provider,
                    type: "custom",
                    credentials: { ...data },
                    default: phase === "install",
                    installationId: installation.id
                  });
                }),
                of(undefined)
              ),
              updatedConnection: iif(
                () => !!isRefresh,
                defer(() => {
                  return this.installationBackend
                    .loadConnectionById(refreshConnectionId!)
                    .pipe(
                      switchMap((connection: Connection) => {
                        return this.installationBackend
                          .updateConnectionCredentials({
                            connectionId: refreshConnectionId!,
                            version: connection.version,
                            type: "custom",
                            credentials: { ...data }
                          })
                          .pipe(
                            switchMap(() =>
                              this.installationBackend.loadConnectionById(
                                refreshConnectionId!
                              )
                            )
                          );
                      })
                    );
                }),
                of(undefined)
              )
            }).pipe(
              map(({ createdConnetion, updatedConnection }) => {
                return updatedConnection || createdConnetion;
              })
            )
          );

          resolve({
            result: ConnectIntegrationConnectionResultType.SUCCEED,
            connection
          });
        },
        onCancel: async () => {
          resolve({
            result: ConnectIntegrationConnectionResultType.CANCELED
          });
        }
      });
    });
  }
}
